import React, { useCallback } from 'react'
import { UseRefinementListProps, useRefinementList } from 'react-instantsearch'

import cn from 'classnames'
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import CheckboxIcon from 'public/images/icons/checkbox-checked.svg'
import SearchBigIcon from 'public/images/icons/search-big-icon.svg'

import Checkbox from 'components/checkbox'

import SearchHighlight from '../search-highlight'
import css from './refinement-list-customized.module.scss'

type RefinementListCustomizedProps = {
  searchable?: boolean
  searchablePlaceholder?: string
  isShowCount?: boolean
  getLabel?: (item: RefinementListItem) => string
} & UseRefinementListProps

const UNLIMITED_RESULTS = 99999

const RefinementListCustomized = ({
  searchable = false,
  searchablePlaceholder = '',
  limit = UNLIMITED_RESULTS,
  isShowCount = true,
  getLabel = (item: RefinementListItem) => item.label,
  ...props
}: RefinementListCustomizedProps) => {
  const { items, refine, searchForItems } = useRefinementList({ limit, ...props })
  const [searchTerm, setSearchTerm] = React.useState('')

  const itemsRefined: RefinementListItem[] = []
  const itemsNotRefined: RefinementListItem[] = []

  items
    .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1))
    .forEach((item) => {
      if (item.isRefined) {
        itemsRefined.push(item)
      } else {
        itemsNotRefined.push(item)
      }
    })

  const renderItem = useCallback(
    (item: RefinementListItem) => {
      const itemId = item.label + props.attribute
      return (
        <li key={itemId} className={cn([css.refinementListItem, item.isRefined && css.refinementListSelectedItem])}>
          <label className={css.refinementListLabel} htmlFor={itemId}>
            <Checkbox
              id={itemId}
              name={item.value}
              className={css.checkboxInput}
              isChecked={item.isRefined}
              onChange={() => refine(item.value)}
              CheckedIcon={<CheckboxIcon />}
            />

            <SearchHighlight text={getLabel(item)} searchTerm={searchTerm} className={css.refinementListLabelText} />
            <span className={cn([css.refinementListCount, !isShowCount && css.refinementListCountHide])}>
              {item.count}
            </span>
          </label>
        </li>
      )
    },
    [getLabel, refine, searchTerm, isShowCount, props.attribute]
  )

  return (
    <div className={css.container}>
      {!!searchable && (
        <div className={css.searchBox}>
          <input
            type="search-box"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={512}
            className={css.searchBoxInput}
            placeholder={searchablePlaceholder}
            onChange={(event) => {
              setSearchTerm(event.currentTarget.value)
              searchForItems(event.currentTarget.value)
            }}
          />
          <div className={css.searchBoxSubmit}>
            <SearchBigIcon className={css.searchBoxSubmitIcon} />
          </div>
        </div>
      )}
      <ul className={css.refinementListList}>{[...itemsRefined, ...itemsNotRefined].map(renderItem)}</ul>
    </div>
  )
}

export default RefinementListCustomized
